import React, { useState, useEffect } from "react";
import { useTimer } from "../context/TimerContext";
import {
  XMarkIcon,
  StopIcon,
  CheckIcon,
  PauseIcon,
  PlayIcon,
  ClockIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
import { formatDate } from "../utils/timeUtils";
import TaskSelector from "./TaskSelector";
import TimerClock from "./TimerClock";
import QuickTaskCreate from "./QuickTaskCreate";
import { useAllTasks } from "../hooks/useAllTasks";
import { useProjects } from "../hooks/useProjects";

/**
 * TimerPanel component - expandable panel for timer controls and details
 * Shows when a timer is active or when user expands it
 */
const TimerPanel = () => {
  const {
    currentTimer,
    formattedTime,
    isLoading,
    stopTimer,
    pauseTimer,
    resumeTimer,
    assignTaskToTimer,
    isTimerPanelOpen,
    toggleTimerPanel,
    refreshCurrentTimer,
  } = useTimer();

  // Get task data from useAllTasks hook (only fetch while the panel is open)
  const { tasks, refetch: refetchTasks } = useAllTasks({
    enabled: isTimerPanelOpen,
  });

  // Get projects directly from useProjects (for more complete project data)
  const { projects, refetch: refetchProjects } = useProjects();

  const [description, setDescription] = useState(
    currentTimer?.description || ""
  );
  const [selectedTask, setSelectedTask] = useState(null);
  const [stopError, setStopError] = useState("");

  // Update description when currentTimer changes
  useEffect(() => {
    if (currentTimer?.description) {
      setDescription(currentTimer.description);
    }
  }, [currentTimer]);

  // Update selected task when a task is assigned to the timer
  useEffect(() => {
    if (currentTimer?.task_id) {
      // If task details are available in the timer response, use them
      if (currentTimer.task_name || currentTimer.task_details) {
        setSelectedTask({
          id: currentTimer.task_id,
          project_id: currentTimer.project_id,
          name:
            currentTimer.task_name ||
            currentTimer.task_details?.name ||
            "Unknown Task",
          status: currentTimer.task_details?.status || "unknown",
        });
      } else {
        // Otherwise just use the IDs
        setSelectedTask({
          id: currentTimer.task_id,
          project_id: currentTimer.project_id,
        });
      }
    } else {
      setSelectedTask(null);
    }
  }, [currentTimer]);

  // Refresh project and task data whenever the panel is opened
  useEffect(() => {
    if (isTimerPanelOpen) {
      // Refresh both projects and tasks data when the panel is opened
      console.log("Timer panel opened - refreshing projects and tasks data");
      refetchProjects();
      refetchTasks();
    }
  }, [isTimerPanelOpen, refetchProjects, refetchTasks]);

  // Don't render if panel is closed
  if (!isTimerPanelOpen) return null;

  // Handler for task selection
  const handleTaskSelect = async (task) => {
    console.log("Task selected:", task);
    setSelectedTask(task);
    setStopError(""); // Clear any errors when a task is selected

    // If timer already exists, assign the task to it
    if (currentTimer && task) {
      try {
        await assignTaskToTimer(task._id ? task._id : task.id, description);
      } catch (error) {
        console.error("Failed to assign task to timer:", error);
      }
    }
  };

  // Handler for newly created task
  const handleTaskCreated = async (newTask) => {
    // Refetch tasks to update the list
    await refetchTasks();

    console.log("New task created:", newTask);
    // Auto-select the newly created task
    handleTaskSelect(newTask);
  };

  // Handler for stopping the timer
  const handleStopTimer = async () => {
    setStopError(""); // Clear any error messages

    try {
      await stopTimer(description);
    } catch (error) {
      console.error("Failed to stop timer:", error);
      setStopError(error.message || "Failed to stop timer");
    }
  };

  // Handler for pausing/resuming the timer
  const handlePauseResumeTimer = async () => {
    setStopError(""); // Clear any error messages

    try {
      if (currentTimer?.status === "paused") {
        console.log("Resuming timer...");
        await resumeTimer();
        // update localStorage status
        localStorage.setItem("timer_status", "running");
      } else {
        console.log("Pausing timer...");
        await pauseTimer(description);
        // update localStorage status
        localStorage.setItem("timer_status", "paused");
      }

    } catch (error) {
      console.error("Failed to pause/resume timer:", error);
      setStopError(error.message || "Failed to pause/resume timer");
    }
  };

  return (
    <div className="fixed top-16 right-4 md:right-8 bg-white dark:bg-darkbg-800 border border-grayteck-200 dark:border-darkbg-700 shadow-lg rounded-lg w-full max-w-sm transition-all duration-300 z-50 overflow-hidden">
      <div className="p-3 border-b border-grayteck-200 dark:border-darkbg-700 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {currentTimer?.status === "paused" ? (
            <PauseIcon className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />
          ) : (
            <ClockIcon className="w-4 h-4 text-grayteck-600 dark:text-grayteck-300" />
          )}
          <span className="font-medium text-grayteck-800 dark:text-grayteck-200">
            Time Tracker {currentTimer?.status === "paused" ? "(Paused)" : ""}
          </span>
          <TimerClock timeEntryId={currentTimer?._id || currentTimer?.id} />
        </div>
        <button
          onClick={toggleTimerPanel}
          className="text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="p-3">
        {/* Task section with explanation */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300">
              Task (Optional)
            </label>
            <span className="text-xs text-grayteck-500 dark:text-grayteck-400">
              Assign a task to track time against
            </span>
          </div>

          <TaskSelector
            onTaskSelect={handleTaskSelect}
            selectedTaskId={currentTimer?.task_id || selectedTask?.id}
          />

          {/* Add quick task creation button and form */}
          {projects && projects.length > 0 ? (
            <QuickTaskCreate
              projects={projects}
              onTaskCreated={handleTaskCreated}
            />
          ) : (
            <div className="mt-2 p-3 border border-grayteck-200 dark:border-darkbg-700 rounded-md bg-grayteck-50 dark:bg-darkbg-900 text-sm text-grayteck-600 dark:text-grayteck-400">
              No projects available. Create a project first to add tasks.
            </div>
          )}
        </div>

        {/* Description */}
        <div className="mb-3">
          <label
            htmlFor="timer-description"
            className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
          >
            Description
          </label>
          <input
            type="text"
            id="timer-description"
            className="w-full px-3 py-2 border border-grayteck-300 dark:border-darkbg-600 rounded-md 
                     bg-white dark:bg-darkbg-800 text-grayteck-800 dark:text-grayteck-200
                     focus:outline-none focus:ring-2 focus:ring-grayteck-500"
            placeholder="What are you working on?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Error display */}
        {stopError && (
          <div className="mb-3 text-sm text-red-600 dark:text-red-400 flex items-center">
            <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{stopError}</span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handlePauseResumeTimer}
            disabled={isLoading || !currentTimer}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm 
                      ${
                        currentTimer?.status === "paused"
                          ? "bg-green-500 hover:bg-green-600"
                          : "bg-yellow-500 hover:bg-yellow-600"
                      } 
                      text-white rounded-md disabled:opacity-50`}
          >
            {currentTimer?.status === "paused" ? (
              <>
                <PlayIcon className="w-3.5 h-3.5" />
                Resume
              </>
            ) : (
              <>
                <PauseIcon className="w-3.5 h-3.5" />
                Pause
              </>
            )}
          </button>
          <button
            onClick={handleStopTimer}
            disabled={isLoading || !currentTimer}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 
                     text-white rounded-md disabled:opacity-50"
          >
            <StopIcon className="w-3.5 h-3.5" />
            Stop Timer
          </button>
        </div>

        {/* Timer info */}
        {currentTimer && (
          <div className="mt-2 text-xs text-grayteck-500 dark:text-grayteck-400 text-right">
            <div>Started: {formatDate(currentTimer.start_time)}</div>
            {currentTimer.pause_count > 0 && (
              <div>
                Paused: {currentTimer.pause_count}{" "}
                {currentTimer.pause_count === 1 ? "time" : "times"}
                {currentTimer.total_pause_duration &&
                  ` (${Math.floor(
                    currentTimer.total_pause_duration / 60
                  )} min ${currentTimer.total_pause_duration % 60} sec)`}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimerPanel;
